import { api } from "./api.js";

/**
 * Friends REST + the personal event stream (invites, friend presence).
 * Account-only — guests get an empty list and no stream.
 */

export interface Friend {
  id: string;
  username: string;
  online: boolean;
}

export type FriendEvent =
  | { type: "invite"; from: string; roomId: string; code: string }
  | { type: "friend_online"; id: string; username: string }
  | { type: "friend_offline"; id: string }
  | { type: "friend_request"; id: string; username: string }
  | { type: "friend_accepted"; id: string; username: string }
  | { type: "online_snapshot"; ids: string[] };

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (api.token) headers.authorization = `Bearer ${api.token}`;
  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `${res.status}`);
  return data;
}

export interface FriendList {
  friends: Friend[];
  incoming: { id: string; username: string }[];
  outgoing: { id: string; username: string }[];
}

export const friends = {
  list: (): Promise<FriendList> => req("GET", "/friends"),
  /** Send a request. May resolve to {status:"accepted"} if they'd requested you. */
  add: (username: string): Promise<{ status?: string; friend?: Friend }> => req("POST", "/friends", { username }),
  accept: (id: string): Promise<{ friend: Friend }> => req("POST", `/friends/${id}/accept`),
  remove: (id: string): Promise<{ ok: boolean }> => req("DELETE", `/friends/${id}`),
  invite: (id: string, roomId: string, code: string): Promise<{ ok: boolean }> =>
    req("POST", `/friends/${id}/invite`, { roomId, code }),
};

// ---- personal event stream (a single shared EventSource) ----
const handlers = new Set<(e: FriendEvent) => void>();
let es: EventSource | null = null;
let esToken = "";

export function onFriendEvent(h: (e: FriendEvent) => void): () => void {
  handlers.add(h);
  return () => handlers.delete(h);
}

/** Open (or reopen after a token change) the personal stream. No-op for guests
 *  and idempotent — calling it repeatedly with the same token does nothing. */
export function startFriendEvents(): void {
  if (api.guest || !api.token) return;
  if (es && esToken === api.token) return; // already open on this token
  if (es) es.close();
  esToken = api.token;
  es = new EventSource(`/api/events?token=${encodeURIComponent(api.token)}`);
  es.onmessage = (ev) => {
    try {
      const e = JSON.parse(ev.data) as FriendEvent;
      for (const h of handlers) h(e);
    } catch {
      /* ignore malformed */
    }
  };
  // EventSource auto-reconnects; nothing else to do on error
}

export function stopFriendEvents(): void {
  es?.close();
  es = null;
  esToken = "";
}
